<template>
  <div
    class="card-groups-picker crdgrpicker text-noselect"
    draggable="false"
    data-gwmenu="noclose"
    @mousedown="handleMousedown()"
    @touchstart="handleMousedown()"
  >
    <div class="crdgrpicker_top">
      <div
        class="ra_table position-relative z-index_2 text-noselect"
        :style="tableStyle"
      >
        <div
          v-for="groupId in allGroups"
          :key="groupId"
          class="ra_table_cell text-noselect"
          style="padding: 0"
          :data-cell-id="groupId"
          :class="{
            ra_table_cell_selected: l_groups.some(id => id === groupId),
          }"
          @touchstart="e => toggle(e, groupId, 'touchstart')"
          @mousedown="e => toggle(e, groupId, 'mousedown')"
          @mouseenter="e => swipeToogleGroup(e, groupId, 'mouseenter')"
          @touchmove="e => swipeToogleGroup(e, groupId, 'touchmove')"
        >
          {{ groupId }}
        </div>
      </div>
    </div>

    <div
      class="d-flex flex-1"
      style="margin: 1rem 0"
    >
      <div class="f-column flex-1">
        <div class="crdgrpicker_slider">
          <RangeInput
            style="--rainput-height: var(--custom-rainput-height, 1rem)"
            :class="rangeInputCssClass"
            :values="[
              {
                id: 1,
                cssStyle: rangeInputStyle.knob_1,
                percentage: rangeInputs[0],
              },
              {
                id: 2,
                cssStyle: rangeInputStyle.knob_2,
                percentage: rangeInputs[1],
              },
              {
                id: 3,
                cssStyle: `--clr-input-back: var(--clr-black-3);`,
                percentage: rangeInputs[2],
              },
            ]"
            @values:update="({ values }) => handleInput(values)"
            @update:wheelend="handleMouseup()"
            @values:updateend="handleMouseup()"
            @click.stop
            @touchstart.stop
          />
        </div>
        <div
          class="crdgrpicker_presets"
          style="z-index: 9"
        >
          <div
            v-for="(nItem, idx) in [
              {
                id: 'from',
                title: $t('common.from'),
                cssStyle: '--clr-focus: var(--clr-error)',
              },
              {
                id: 'to',
                title: $t('common.to'),
                cssStyle: '--clr-focus: var(--clr-primary)',
              },
            ]"
            :key="nItem.id"
            class="f-center input-cont"
          >
            <div class="pr-2 weight_500">
              {{ nItem.title }}
            </div>

            <div
              class="input-cont csst"
              @mouseenter="
                e => fl_positionTooltip(e, { offset: 0, placement: 'top', strategy: 'absolute' })
              "
            >
              <NumberInput
                class="z-index_2"
                :value="
                  numFixed(
                    (idx === 0 ? rangeInputs[0] : rangeInputs[0] + rangeInputs[1]) * 100,
                    idx,
                  )
                "
                :min="0"
                :max="100"
                :step="1"
                style="--input-height: 1.75rem"
                :style="nItem.cssStyle"
                @input="
                  num => {
                    handleNumInput(num / 100, idx)
                    handleMouseup()
                  }
                "
              />
              <GwBtns
                :options="presets"
                style="position: absolute; --gwbtn-padding: 0 0.25rem; padding: 0.25rem"
                class="csst_content csst_content_hoverable gwbtns_horizontal_grey"
                @click="
                  (_e, num) => {
                    handleNumInput(idx === 0 ? num : num - rangeInputs[0], idx)
                    handleMouseup()
                  }
                "
              >
                <template #default="{ item }">
                  {{ item * 100 }}
                </template>
              </GwBtns>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { nextTick } from 'vue'
import { balanceValues } from '@/helpers/helpers'
import { rangeTableGroups } from '@/helpers/solution'
import NumberInput from '@/components/lib/NumberInput'
import format_mix from '@/mixins/format_mix'
import float_mix from '@/mixins/float_mix'
import GwBtns from '@/components/lib/GwBtns'
import RangeInput from '@/components/lib/RangeInput'
import cgp_dict from './cgp_dict.json'

export default {
  name: 'CardGroupesPicker',
  components: {
    NumberInput,
    RangeInput,
    GwBtns,
  },
  mixins: [format_mix, float_mix],
  props: {
    rangeInputCssClass: String,
    groups: { type: Array, default: () => [] },
    tableStyle: String,
    rangeInputStyle: {
      type: Object,
      default: () => ({
        knob_1: `--clr-input-back: var(--clr-black-3); --clr-input-handle: var(--clr-error)`,
        knob_2: `--clr-input-back: var(--clr-primary); --clr-input-handle: var(--clr-primary)`,
      }),
    },
    localKey: String,
  },
  emits: ['update:groups'],
  data() {
    return {
      l_groups: [],
      mousedown: false,
      toggleValue: false,
      rangeInputs: [0, 1, 0],
      presets: [0, 0.25, 0.5, 0.75, 1],
      allGroups: rangeTableGroups(),
      inputs: [0, 1],
    }
  },
  mounted() {
    nextTick(() => {
      if (this.localKey) {
        const inputs = JSON.parse(localStorage.getItem(this.localKey) || '[0, 1]')
        if (inputs[0] >= 0 && inputs[1] <= 1) {
          const { groups } = this
          this.inputs = inputs
          nextTick(() => {
            this.setLocalGroups(groups)
          })
        }
      }
    })
  },
  beforeUnmount() {
    this.removeEventListeners()
  },
  methods: {
    handleInput(values) {
      this.rangeInputs = values.map(i => i.percentage)
      this.setLocalGroupsByFrequency()
    },
    handleNumInput(num, index) {
      num = this.numFixed(num, 10)
      if (num < 0) {
        this.handleNumInput(this.rangeInputs[0] + num, 0)
        num = 0
      }
      let res = [...this.rangeInputs]
      res = res.map((i, idx) => {
        if (idx === index) {
          return num
        }
        return i
      })

      res = balanceValues(res, index)
      this.rangeInputs = res
      this.setLocalGroupsByFrequency()
    },
    persistInputs() {
      if (this.localKey) {
        localStorage.setItem(this.localKey, JSON.stringify(this.inputs))
      }
    },
    handleMousedown() {
      this.mousedown = true
      window.addEventListener('mouseup', this.handleMouseup)
      window.addEventListener('touchend', this.handleMouseup)
    },
    emitUpdate() {
      this.$emit('update:groups', this.l_groups)
    },
    handleMouseup() {
      this.mousedown = false
      this.removeEventListeners()
      this.emitUpdate()
    },
    removeEventListeners() {
      window.removeEventListener('mouseup', this.handleMouseup)
      window.removeEventListener('touchend', this.handleMousemove)
    },
    setLocalGroupsByFrequency() {
      const freq_from = this.l_frequency[0]
      const freq_to = this.l_frequency[1] + freq_from
      // console.log(freq_from, freq_to)
      const res = cgp_dict.filter(i => i[1] >= freq_from && i[1] <= freq_to).map(i => i[0])
      this.setLocalGroups(res)
    },
    toggle(e, groupId) {
      e.preventDefault()
      this.toggleValue = this.l_groups.some(id => id === groupId)
      this.setValue(groupId)
    },
    setValue(groupId) {
      if (this.toggleValue) {
        const res = this.l_groups.filter(id => id !== groupId)
        this.setLocalGroups(res)
        return
      }
      const res = [...this.l_groups, groupId]
      this.setLocalGroups(res)
    },
    swipeToogleGroup(e, group) {
      if (this.mousedown) {
        if (e.touches?.[0]) {
          const cell = document.elementFromPoint(e.touches[0].clientX, e.touches[0].clientY)
          group = cell?.attributes?.['data-cell-id']?.value
          if (!group) return
        }
        this.setValue(group)
      }
    },
    setLocalGroups(res) {
      this.l_groups = [...new Set(res)]
      if (this.l_groups.length === 169) {
        if (this.l_frequency[0] !== 0 || this.l_frequency[1] !== 1) {
          this.handleNumUpdate([0, 1])
        }
      }
    },
  },
  computed: {
    l_frequency() {
      return [this.numFixed(this.rangeInputs[0], 2), this.numFixed(this.rangeInputs[1], 2)]
    },
    l_groupsFormatted() {
      const res = this.groups.map(i => i.id).join(', ')
      return res
    },
    totalCombos() {
      const res = this.l_groups.reduce(
        (a, b) => a + (b.includes('o') ? 12 : b.includes('s') ? 4 : 6),
        0,
      )
      return res
    },
  },
  watch: {
    groups: {
      immediate: true,
      handler: 'setLocalGroups',
    },
  },
}
</script>

<style lang="scss" scoped>
.input-cont {
  position: relative;
  display: flex;
  justify-content: center;
  &_btns {
    background: var(--clr-black-1-3);
    border-radius: 4px;
    display: flex;
    grid-gap: 0.25rem;
    &_btn {
      min-width: 2.75rem;
    }
  }
}
.crdgrpicker {
  position: relative;
  &_top {
    display: flex;
    gap: 1rem;
    z-index: 2;
    position: relative;
  }
  &_qoptions {
    display: flex;
    flex-direction: column;
    &_item {
      justify-content: flex-start;
    }
    gap: 0.5rem;
  }
  &_slider {
    z-index: 1;
    z-index: 1;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    &:hover {
      z-index: 3;
    }
  }
  &_presets {
    position: relative;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    padding: 1rem 0 0 0;
    grid-gap: 1rem;
    &_btn {
      padding: 0.25rem 0.5rem;
      margin: 0 0.125rem;
      min-width: 2.5rem;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      border-radius: 4px;
      --clr-hover: var(--clr-black-4);
      color: var(--clr-white-0);
      background: var(--clr-black-2);
      &:hover {
        background: var(--clr-hover);
      }
    }
  }
}
.ra_table {
  --border-width: 1px;
  max-width: 100vw;
  flex: 1;
  display: grid;
  grid-template-columns: repeat(13, 1fr);
  grid-template-rows: repeat(13, 1fr);
  overflow: hidden;
  border-radius: 4px;
  --clr-back: var(--clr-black-0);
  background: var(--clr-back);
  border: var(--border-width) var(--clr-back) solid;
  touch-action: none;

  --rtc-clr-back-hover: var(--clr-primary-2-2);
  --rtc-clr-back-active: var(--clr-primary-2);
  --rtc-clr-hover: var(--clr-white-0);
  --rtc-clr-active: var(--clr-white-0);

  &_cell {
    outline: 1px var(--clr-back) solid;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    min-height: var(--rtc-min-height, 2rem);
    background: var(--clr-black-1);
    transition: 0.2s;
    cursor: pointer;
    color: var(--clr-grey-2);
    font-size: 0.8rem;
    font-weight: 500;
    &_selected {
      background: var(--rtc-clr-back-active);
      color: var(--rtc-clr-active);
    }
  }
}
@media (hover: hover) and (pointer: fine) {
  .ra_table {
    &_cell:not(.ra_table_cell_selected) {
      &:hover {
        background: var(--rtc-clr-back-hover);
        color: var(--rtc-clr-hover);
      }
    }
  }
}
</style>
